import { useMemo, useState } from 'react'
import { cn } from '../lib/cn'

const SIZES = {
  xs: 'size-5',
  sm: 'size-7',
  md: 'size-10',
  lg: 'size-20',
} as const

/**
 * Temporary faces, generated — muted duotone discs with initials, picked by
 * hashing the address so a person keeps theirs everywhere. They stand in
 * until the Google photo arrives; like every photo they are just an image,
 * so a missing picture never reads as a broken one.
 */
const TEMP_GROUNDS: ReadonlyArray<readonly [string, string]> = [
  ['#4a5568', '#2d3748'],
  ['#556b5d', '#37473e'],
  ['#6b5d55', '#473e37'],
  ['#555a6b', '#373c47'],
  ['#5d556b', '#403747'],
  ['#55636b', '#374147'],
  ['#6b6255', '#474137'],
  ['#5d6b55', '#3e4737'],
]

function tempFace(name: string | null, email: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  const initials = words.length >= 2
    ? `${words[0]!.charAt(0)}${words[words.length - 1]!.charAt(0)}`
    : ((name ?? email).trim().charAt(0) || '?')
  const seed = `${email}${name ?? ''}`
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) % 9973
  const [from, to] = TEMP_GROUNDS[hash % TEMP_GROUNDS.length]!
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>` +
    `</linearGradient></defs>` +
    `<rect width="64" height="64" fill="url(#g)"/>` +
    `<text x="32" y="32" dy="0.36em" text-anchor="middle" font-family="Georgia, serif" ` +
    `font-size="26" fill="#f4f2ec">${initials.toUpperCase().replace(/[<>&"']/g, '')}</text></svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

/**
 * A person, as a face when there is one and a generated stand-in when there
 * is not. The platform holds no pictures of its own — the picture, when
 * present, is the Google profile's URL carried on the account row, shown by
 * the browser that already caches it. A photo that fails to load falls back
 * to the stand-in, so nobody ever wears a broken image.
 */
export function Avatar({
  name,
  email,
  src,
  size = 'md',
  className,
}: {
  name: string | null
  email: string
  src?: string | null
  size?: keyof typeof SIZES
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const showPicture = src && !failed
  const standIn = useMemo(() => tempFace(name, email), [name, email])
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-tint',
        SIZES[size],
        className,
      )}
    >
      {showPicture ? (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <img src={standIn} alt="" draggable={false} className="size-full" />
      )}
    </span>
  )
}
